import { Flame, AlertTriangle } from "lucide-react";

export const PRESENCE_TONES = {
  Available: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300",
  Active: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300",
  Busy: "bg-rose-500/10 text-rose-700 ring-rose-500/20 dark:text-rose-300",
  "Do not disturb": "bg-rose-500/10 text-rose-700 ring-rose-500/20 dark:text-rose-300",
  "In a meeting": "bg-rose-500/10 text-rose-700 ring-rose-500/20 dark:text-rose-300",
  Away: "bg-amber-500/10 text-amber-700 ring-amber-500/20 dark:text-amber-300",
  Reassigning: "bg-sky-500/10 text-sky-700 ring-sky-500/20 dark:text-sky-300",
};

export const presenceTone = (v) =>
  PRESENCE_TONES[v] || "bg-slate-500/10 text-slate-500 ring-slate-500/15 dark:text-slate-300";

export const fmtShiftTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

/** "in 3h 20m" / "in 2 days" until a handover. */
export const untilText = (iso) => {
  if (!iso) return "";
  const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (Number.isNaN(mins)) return "";
  if (mins <= 0) return "now";
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `in ${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return `in ${days} day${days === 1 ? "" : "s"}`;
};

/** Tooltip text for the on-call chip, including the next handover when we know it. */
export const handoverTitle = (row) => {
  if (!row?.on_call) return "Not on call";
  const h = row.on_call_handover;
  if (!h) return "On call via incident.io";
  const base = `On call — ${h.schedule || "incident.io"}`;
  const ends = h.ends_at ? `until ${fmtShiftTime(h.ends_at)} (${untilText(h.ends_at)})` : "";
  const next = h.next_name ? `hands over to ${h.next_name}` : "no one scheduled after this shift";
  return [base, ends, next].filter(Boolean).join(" · ");
};

/** Amber marker: Teams and Intercom disagreed, so we left this person's status alone. */
export const conflictTitle = (row) =>
  `Teams says ${row.teams_status || "away"}, Intercom says ${row.intercom_status || "Active"} — ` +
  "status left unchanged so we don't mark them Out while they're working the inbox.";

export const ConflictBadge = ({ row, testid, className = "" }) =>
  row?.presence_conflict ? (
    <span data-testid={testid} title={conflictTitle(row)}
      className={`inline-flex items-center gap-0.5 rounded-full bg-amber-400/20 px-1 py-0.5 text-[9px] font-bold text-amber-700 ring-1 ring-amber-500/40 dark:text-amber-300 ${className}`}>
      <AlertTriangle className="h-2.5 w-2.5" />
    </span>
  ) : null;

const Chip = ({ label, value, tone, title, testid, href }) => {
  const inner = (
    <>
      <span className="block text-[8px] font-bold uppercase leading-none tracking-wider opacity-60">{label}</span>
      <span className="mt-0.5 block truncate text-[10px] font-semibold leading-none">{value || "—"}</span>
    </>
  );
  const cls = `block w-full rounded-md px-1 py-1 text-center ring-1 ring-inset ${tone} ${
    href ? "transition-transform hover:scale-[1.03]" : ""}`;
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" title={title} data-testid={testid} className={cls}>{inner}</a>
  ) : (
    <span title={title} data-testid={testid} className={cls}>{inner}</span>
  );
};

/** Uniform Teams / Intercom / On-call chip row, shared by both card layouts. */
export const PresenceChips = ({ row, appId, prefix = "presence" }) => {
  const teams = (row.teams_status || "").trim();
  const intercom = (row.intercom_status || "").trim();
  const h = row.on_call_handover;
  return (
    <div className="relative grid grid-cols-3 gap-1" data-testid={`${prefix}-chips-${row.user_id}`}>
      <Chip label="Teams" value={teams} tone={presenceTone(teams)} testid={`${prefix}-teams-${row.user_id}`}
        title={teams ? `Microsoft Teams: ${teams}` : "No Teams presence available"} />
      <Chip label="Intercom" value={intercom} tone={presenceTone(intercom)} testid={`${prefix}-intercom-${row.user_id}`}
        title={intercom ? `Intercom: ${intercom}` : "Not an Intercom teammate"} />
      <Chip label="On call" value={row.on_call ? (h?.ends_at ? untilText(h.ends_at).replace("in ", "") : "On") : ""}
        testid={`${prefix}-oncall-${row.user_id}`}
        tone={row.on_call ? "bg-orange-500/10 text-orange-600 ring-orange-500/25 dark:text-orange-300"
                          : presenceTone("")}
        title={handoverTitle(row)} />
      {row.presence_conflict && (
        <ConflictBadge row={row} testid={`${prefix}-conflict-${row.user_id}`}
          className="absolute -right-1 -top-2 shadow-sm" />
      )}
    </div>
  );
};

/** Muted traffic-light dot colour for a Teams/Intercom status. */
export const presenceDotColor = (v) => {
  const s = (v || "").toLowerCase();
  if (["available", "active"].includes(s)) return "#16a34a";
  if (["away", "reassigning", "be right back"].includes(s)) return "#eab308";
  if (["busy", "do not disturb", "in a meeting", "on the phone"].includes(s)) return "#dc2626";
  return "#cbd5e1";
};

const Bubble = ({ label, dot, title, testid, href }) => {
  const inner = (
    <span title={title} data-testid={testid}
      className="inline-flex items-center gap-0.5 rounded-full bg-muted/60 px-1 py-0 text-[8px] font-bold uppercase tracking-wide text-muted-foreground ring-1 ring-inset ring-border">
      <span className="h-1 w-1 rounded-full" style={{ backgroundColor: dot }} />{label}
    </span>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer">{inner}</a> : inner;
};

/** Compact T / HD / OC bubbles with traffic-light dots (dense layouts). */
export const PresenceBubbles = ({ row, prefix = "presence" }) => {
  const teams = (row.teams_status || "").trim();
  const intercom = (row.intercom_status || "").trim();
  return (
    <div className="flex items-center gap-1" data-testid={`${prefix}-bubbles-${row.user_id}`}>
      <Bubble label="T" dot={presenceDotColor(teams)} testid={`${prefix}-teams-${row.user_id}`}
        title={teams ? `Microsoft Teams: ${teams}` : "No Teams presence"} />
      <Bubble label="HD" dot={presenceDotColor(intercom)} testid={`${prefix}-intercom-${row.user_id}`}
        title={intercom ? `Help desk (Intercom): ${intercom}` : "Not an Intercom teammate"} />
      <Bubble label="OC" dot={row.on_call ? "#f97316" : "#cbd5e1"} testid={`${prefix}-oncall-${row.user_id}`}
        title={handoverTitle(row)} />
      {row.presence_conflict && <ConflictBadge row={row} testid={`${prefix}-conflict-${row.user_id}`} />}
    </div>
  );
};

export const OnCallFlame = ({ className = "" }) => <Flame className={className} fill="currentColor" />;
